import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { useEffect, useState } from 'react'

import { FieldWrapper } from '../../../Components/Form'
import { FC } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { showToast } from '../../../utils/toast'

const language = cap.language.state || 'IT'
const t = translations[language]

const EMAIL_REGEX = /^[a-z0-9._%+-]+@[a-z0-9-]+(\.[a-z0-9-]+)+$/i

/**
 * Contenuto del dialog per inviare una segnalazione operatore
 * @param {Object} props
 * @param {Function} props.onHide - Chiude il dialog
 */
export function ModalSegnalazione({ onHide }) {
  const [reportText, setReportText] = useState('')
  const [emailInput, setEmailInput] = useState('')
  // Dominio fisso dell'environment: se presente l'operatore scrive solo la parte prima della "@"
  const [emailDomain, setEmailDomain] = useState('')
  const [emailTouched, setEmailTouched] = useState(false)
  const [isLoadingContext, setIsLoadingContext] = useState(true)
  const [isSending, setIsSending] = useState(false)

  // Precompila con l'ultima email indicata dall'operatore (salvata al primo invio)
  useEffect(() => {
    let cancelled = false
    FC.service('info')
      .get('getReportContext')
      .then(({ emailDomain: domain = '', contactEmail = '' } = {}) => {
        if (cancelled) return
        setEmailDomain(domain)
        setEmailInput(domain ? contactEmail.split('@')[0] : contactEmail)
      })
      .catch(() => {})
      .finally(() => !cancelled && setIsLoadingContext(false))
    return () => {
      cancelled = true
    }
  }, [])

  const contactEmail = emailDomain ? `${emailInput.trim()}@${emailDomain}` : emailInput.trim()
  const isEmailValid = EMAIL_REGEX.test(contactEmail)
  const showEmailError = emailTouched && !isEmailValid

  // Con dominio fisso, un indirizzo incollato per intero si riduce alla parte locale
  const handleEmailChange = (value) => setEmailInput(emailDomain ? value.split('@')[0] : value)

  const sendReport = async () => {
    if (isSending) return
    setIsSending(true)
    try {
      const currentUrl = window.location.href
      await FC.service('info').create({
        action: 'sendReport',
        reportText,
        conversationUrl: currentUrl,
        contactEmail
      })
      showToast('success', t.gfSegnalazioneInviata, t.gfSegnalazioneInviataSuccesso)
    } catch (e) {
      showToast('error', t.errore, e?.message || t.gfErroreInvioSegnalazione)
    } finally {
      setIsSending(false)
      onHide()
    }
  }

  return (
    <>
      <div className="gf-dialog-modal__body">
        <FieldWrapper id="report-notes" label={t.gfNoteSegnalazione}>
          <InputTextarea
            id="report-notes"
            value={reportText}
            onChange={(e) => setReportText(e.target.value)}
            rows={5}
            autoResize
            disabled={isSending}
          />
        </FieldWrapper>
        <FieldWrapper
          id="report-email"
          label={t.gfEmailOperatoreSegnalazione}
          required
          helper={t.gfEmailOperatoreSegnalazioneHint}
          error={showEmailError ? t.gfEmailOperatoreSegnalazioneNonValida : undefined}
          disabled={isSending || isLoadingContext}
        >
          <div className={emailDomain ? 'p-inputgroup' : undefined}>
            <InputText
              id="report-email"
              inputMode="email"
              autoComplete="email"
              value={emailInput}
              onChange={(e) => handleEmailChange(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              disabled={isSending || isLoadingContext}
              aria-invalid={showEmailError ? 'true' : 'false'}
              className={showEmailError ? 'p-invalid' : ''}
            />
            {emailDomain && <span className="p-inputgroup-addon">@{emailDomain}</span>}
          </div>
        </FieldWrapper>
      </div>
      <div className="gf-dialog-modal__footer">
        <button type="button" className="gf-btn" onClick={onHide} disabled={isSending}>
          {t.annulla}
        </button>
        <button
          type="button"
          className="gf-btn gf-btn--primary"
          onClick={sendReport}
          disabled={isSending || isLoadingContext || !reportText.trim() || !isEmailValid}
        >
          {isSending ? <span className="gf-btn__spinner" aria-hidden="true" /> : null}
          {t.gfInvia}
        </button>
      </div>
    </>
  )
}
